import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { ApprovalDecision } from '@treasury/domain/approvals/types';
import { StopPaymentsService } from '@treasury/domain/stop-payments/stop-payments.service';
import { UsersService } from '@treasury/domain/users';
import { LabeledList, NotificationService, TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-blocking-loader';
import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-footer';
// import { format } from 'date-fns/format';
import { NavigationService } from '@treasury/core/navigation';
import { TmApiError } from '@treasury/domain/shared';
import { dashboardIcon } from '@treasury/presentation/assets/icons';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { format } from 'date-fns/format';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import '../../components/jhd-status';
import { showApproveRejectPrompt } from '../../pending-approvals/partials';
import { StopPaymentVm } from '../data/stop-payment.vm';

export const tagName = 'stop-payment-detail-sheet';
@customElement(tagName)
export class StopPaymentDetailSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public open = false;

    @property({ type: Boolean })
    public useNewFlow = false;

    @property({ type: Boolean, reflect: true, attribute: 'is-approving' })
    public isApproving = false;

    @property({ type: Object })
    public stopPayment!: StopPaymentVm;

    @InjectProperty()
    declare private dialogService: TmDialogService;

    @InjectProperty()
    declare private navigationService: NavigationService;

    @state()
    private approversOpen = false;

    @state()
    private auditOpen = false;

    @state()
    private loadingApprovals = false;

    @state()
    private completedApprovals: ApprovalDecision[] = [];

    @state()
    private availableApprovers: string[] = [];

    @state()
    private loading = false;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @InjectProperty()
    declare private usersService: UsersService;

    @InjectProperty()
    declare private stopPaymentsService: StopPaymentsService;

    public updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('open') && this.open) {
            this.getApprovalInformation();
        }
    }

    protected render() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            .flex=${true}
            @close=${() => {
                this.close();
            }}
            ><span slot="header-center">Stop Payment Detail</span>
            <span slot="header-right"
                ><button
                    title="Return to dashboard"
                    class="header-button inline-block relative cursor-pointer border-none outline-none p-2 last-of-type:mr-0 focus:outline-none"
                    @click=${() => this.navigationService.navigate('/dashboard')}
                >
                    <tm-icon
                        .icon=${dashboardIcon}
                        color="var(--jh-color-gray-0)"
                    ></tm-icon></button
            ></span>
            ${this.isApproving
                ? this.renderApprovalInformation()
                : nothing}${this.renderStopPaymentDetails()}${this.isApproving
                ? nothing
                : this.renderApprovalInformation()}
            ${this.renderButtonBar()}${this.renderBlockingLoader()}
        </tm-bottom-sheet>`;
    }

    private async getApprovalInformation() {
        if (this.stopPayment?.status === 'Pending Approval') {
            this.loadingApprovals = true;
            const { completedApprovals, availableApprovers } =
                await this.usersService.fetchAvailableAndCompletedApprovers({
                    approvalEntity: 'stopPayment',
                    productId: this.stopPayment.id,
                    createdBy: parseInt(this.stopPayment.createdBy),
                });
            this.loadingApprovals = false;
            this.completedApprovals = completedApprovals;
            this.availableApprovers = availableApprovers;
        }
    }

    private close() {
        this.open = false;
        this.auditOpen = false;
        this.approversOpen = false;
        this.dispatchEvent(new CustomEvent('close'));
    }

    private renderApprovalInformation() {
        if (this.stopPayment?.status !== 'Pending Approval') {
            return nothing;
        }
        return html`<tm-section
            class=${classMap({
                'mt-3': !this.isApproving,
                'mb-3': this.isApproving,
            })}
            ><treasury-pending-approval-template
                .approvalItem=${this.stopPayment}
                .loading=${this.loadingApprovals}
                .approvals=${this.completedApprovals}
                .eligibleApprovers=${this.availableApprovers}
                .eligibleApproversVisible=${this.approversOpen}
                @eligible-approvers-visible=${(e: CustomEvent) => {
                    this.approversOpen = e.detail;
                }}
            ></treasury-pending-approval-template
        ></tm-section>`;
    }

    private renderStopPaymentDetails() {
        if (!this.stopPayment) {
            return nothing;
        }
        const canCancel =
            this.stopPayment?.permissions.some(
                (p: { permission: string }) => p.permission === 'Cancel'
            ) &&
            this.stopPayment.status !== 'Expired' &&
            this.stopPayment.status !== 'Cancelled';
        return html`<div class="stopPayment-detail-wrapper pl-6 pr-4 py-3 bg-primary">
            <div class="detail-header flex items-start justify-between py-4">
                <div class="detail-amount">
                    <div class="text-lg font-medium mb-1">
                        ${this.stopPayment.checkNumbers.length > 1 ? 'Checks' : 'Check'}
                        <span class="font-normal text-secondary">
                            ${this.stopPayment.checkNumbers[0]}
                            ${this.stopPayment.checkNumbers.length > 1
                                ? html` - ${this.stopPayment.checkNumbers.at(-1)}`
                                : nothing}
                        </span>
                    </div>
                    <div class="text-3xl font-normal">
                        ${new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        }).format(this.stopPayment.checkAmount)}
                    </div>
                </div>

                <div class="detail-status flex items-center my-1 text-xs">
                    <jhd-status .status=${this.stopPayment.status} showLabel></jhd-status>
                </div>
            </div>
            <div class="detail-content py-4 border-t border-dashed">
                <tm-labeled-list
                    .list=${new LabeledList(
                        {
                            account: this.stopPayment.account.accountDisplayLabel,
                            checkNumber: this.stopPayment.checkNumbers,
                            checkDate: this.stopPayment.checkDate
                                ? format(new Date(this.stopPayment.checkDate), 'P')
                                : undefined,
                            checkAmount: this.stopPayment.checkAmount.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                            }),
                            payee: this.stopPayment.payee,
                            expirationDate: this.stopPayment.expirationDate
                                ? format(new Date(this.stopPayment.expirationDate), 'P')
                                : undefined,
                            remarks: this.stopPayment.remarks,
                            createdDate: format(new Date(this.stopPayment.createdDate), 'Pp'),
                            enteredBy: this.stopPayment.enteredBy,
                            sequenceNumber: this.stopPayment.sequenceNumber,
                        },
                        [
                            'account',
                            'checkNumber',
                            'checkDate',
                            'checkAmount',
                            'payee',
                            'expirationDate',
                            'remarks',
                            'createdDate',
                            'enteredBy',
                            'sequenceNumber',
                        ],
                        {
                            checkNumber: 'Check Number(s)',
                            checkDate: 'Check Date(s)',
                            createdDate: 'Stop Payment Date',
                            enteredBy: 'Stopped by',
                            sequenceNumber: 'Sequence ID',
                        }
                    )}
                ></tm-labeled-list>
            </div>
            ${canCancel
                ? html`<tm-button
                      importance="secondary"
                      @click=${() => {
                          this.showCancel();
                      }}
                      >Cancel</tm-button
                  >`
                : nothing}
        </div>`;
    }

    private renderButtonBar() {
        if (!this.stopPayment) {
            return nothing;
        }
        const canApprove = this.stopPayment?.permissions.some(
            (p: { permission: string }) => p.permission === 'Approve'
        );

        const canReject = this.stopPayment?.permissions.some(
            (p: { permission: string }) => p.permission === 'Reject'
        );

        const rejectButton = canReject
            ? {
                  error: true,
                  text: 'Reject',
                  classes: 'w-full m-1',
                  onClick: () => {
                      if (this.useNewFlow) {
                          this.dispatchEvent(
                              new CustomEvent('initiate-decision', {
                                  detail: ApprovalDecision.Reject,
                              })
                          );
                          return;
                      }
                      this.showApproveReject('reject');
                  },
              }
            : undefined;
        const approveButton = canApprove
            ? {
                  success: true,
                  text: 'Approve',
                  classes: 'w-full m-1',
                  onClick: () => {
                      if (this.useNewFlow) {
                          this.dispatchEvent(
                              new CustomEvent('initiate-decision', {
                                  detail: ApprovalDecision.Approve,
                              })
                          );
                          return;
                      }
                      this.showApproveReject('approve');
                  },
              }
            : undefined;

        const buttonConfig = [rejectButton, approveButton].filter(b => b);

        if (!canApprove && !canReject) {
            return nothing;
        }
        return html`<tm-footer
            .direction=${buttonConfig.length === 2 ? 'row' : 'column'}
            .buttonConfig=${buttonConfig}
        >
        </tm-footer>`;
    }

    private async showCancel() {
        let comments = '';
        const { reason } = await this.dialogService.open<void>(
            () =>
                html`<div class="p-3">
                    <p>Are you sure you want to cancel this stop payment?</p>
                    <div class="mt-2">
                        <tm-text-field
                            label="Comments"
                            @value-changed=${(e: CustomEvent) => {
                                comments = e.detail.value;
                            }}
                        ></tm-text-field>
                    </div>
                </div>`,
            'Cancel Stop Payment',
            {
                buttons: {
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Confirm',
                    },

                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                    },
                },
            }
        );
        if (reason === DynamicComponentExitReason.Confirm) {
            try {
                await this.stopPaymentsService.cancelStopPayment(this.stopPayment.id, comments);
                this.notificationService.renderSuccess('Stop payment cancelled successfully');
                this.dispatchEvent(new CustomEvent('update-stop-payment'));
                this.close();
            } catch (e: unknown) {
                this.notificationService.renderError(e as TmApiError);
            }
        }
    }

    private async showApproveReject(action: 'approve' | 'reject') {
        const { reason } = await showApproveRejectPrompt('Stop Payment', action);
        if (reason === DynamicComponentExitReason.Confirm) {
            if (action === 'approve') {
                try {
                    this.loading = true;
                    await this.stopPaymentsService.approveStopPayment(this.stopPayment.id, '');
                    this.notificationService.renderSuccess('Stop payment approved successfully');
                    this.close();
                    this.dispatchEvent(new CustomEvent('update-stop-payment'));
                } catch (e: unknown) {
                    this.notificationService.renderError(e as TmApiError);
                } finally {
                    this.loading = false;
                }
            }
            if (action === 'reject') {
                try {
                    this.loading = true;
                    await this.stopPaymentsService.rejectStopPayment(this.stopPayment.id, '');
                    this.notificationService.renderSuccess('Stop payment rejected successfully');
                    this.close();
                    this.dispatchEvent(new CustomEvent('update-stop-payment'));
                } catch (e: unknown) {
                    this.notificationService.renderError(e as TmApiError);
                } finally {
                    this.loading = false;
                }
            }
        }
    }

    private renderBlockingLoader() {
        if (!this.loading) {
            return nothing;
        }
        return html`<tm-blocking-loader></tm-blocking-loader>`;
    }

    public static get styles() {
        return [
            css`
                .detail-status svg {
                    display: inline-block;
                }
                .stopPayment-data-wrapper {
                    background-color: #fff;
                    border-bottom-left-radius: 5px;
                    border-bottom-right-radius: 5px;
                    min-height: 300px;
                }
                .detail-amount,
                .status {
                    color: var(--header-text-color);
                }
                .header-icon path {
                    fill: var(--content-light);
                }
                .audit-date {
                    color: var(--secondary-text-color, #444444);
                }
                .max-h-36 {
                    max-height: 9rem;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: StopPaymentDetailSheet;
    }
}
